<template>
  <div class="page-wrapper kb-page">
    <div class="content container-fluid">
      <div class="kb-page-head">
        <div class="kb-page-head-copy">
          <span class="kb-eyebrow">AI KNOWLEDGE BASE</span>
          <h3>Knowledge Base</h3>
          <p>Kelola dan tinjau dokumen yang digunakan oleh Chat AI untuk menjawab pertanyaan pelanggan.</p>
        </div>
        <div class="kb-page-head-actions">
          <button
            type="button"
            class="btn kb-btn-ghost"
            :disabled="docsLoading || summaryLoading"
            @click="refreshAll"
          >
            <i class="fa fa-refresh" :class="{ 'fa-spin': docsLoading || summaryLoading }"></i>
            Muat ulang
          </button>
          <button type="button" class="btn kb-btn-primary" data-tour-step="2" @click="openUpload">
            <i class="fa fa-plus"></i>
            Upload Dokumen
          </button>
        </div>
      </div>

      <div class="kb-license-card">
        <div class="kb-license-select">
          <span class="kb-field-label">Lisensi WhatsApp</span>
          <multiselect
            v-model="selectedLicense"
            label="license_name"
            track-by="id"
            placeholder="Pilih lisensi WhatsApp"
            :options="licenseOptions"
            :multiple="false"
            :searchable="true"
            :loading="licenseLoading"
            :internal-search="false"
            :close-on-select="true"
            :options-limit="300"
            :limit="3"
            :limit-text="limitText"
            :max-height="600"
            :show-no-results="true"
            :show-labels="false"
            :custom-label="customLabel"
            open-direction="bottom"
            @search-change="searchLicenses"
            @input="onLicenseChange"
          >
            <template slot="option" slot-scope="{ option }">
              <span>{{ option.license_name }} ({{ option.license_phone_number }})</span>
            </template>
            <span slot="noResult">Lisensi tidak ditemukan.</span>
          </multiselect>
        </div>
        <div v-if="selectedLicense" class="kb-license-meta">
          <span class="kb-connection-dot" :class="{ 'is-online': selectedLicense.is_online_status == 1 }"></span>
          <div>
            <strong>{{ selectedLicense.license_name || "Tanpa nama" }}</strong>
            <small>{{ selectedLicense.license_phone_number || "-" }}</small>
          </div>
        </div>
        <div class="kb-quota-pill" :class="{ 'is-limit': isLimitReached }">
          <i class="fa fa-book"></i>
          <span><strong>{{ docs.length }}</strong> / 5 KB</span>
        </div>
      </div>

      <div class="kb-summary-grid">
        <div class="kb-stat-card">
          <span class="kb-stat-icon is-orange"><i class="fa fa-folder-open-o"></i></span>
          <div>
            <strong>{{ formatNumber(docs.length) }}</strong>
            <small>Dokumen</small>
          </div>
        </div>
        <div class="kb-stat-card">
          <span class="kb-stat-icon is-blue"><i class="fa fa-align-left"></i></span>
          <div>
            <strong>{{ summaryValue(summary && summary.chunk_count) }}</strong>
            <small>Bagian Teks</small>
          </div>
        </div>
        <div class="kb-stat-card">
          <span class="kb-stat-icon is-green"><i class="fa fa-check-circle"></i></span>
          <div>
            <strong>{{ summaryValue(summary && summary.ready_chunk_count) }}</strong>
            <small>Berhasil Disimpan</small>
          </div>
        </div>
        <div class="kb-stat-card">
          <span class="kb-stat-icon is-yellow"><i class="fa fa-exclamation-triangle"></i></span>
          <div>
            <strong>{{ summaryValue(summary && summary.attention_count) }}</strong>
            <small>Perlu Perhatian</small>
          </div>
        </div>
      </div>

      <div v-if="queueSummary.documents" class="kb-queue-strip" :class="{ 'is-active': queueSummary.active }">
        <span class="kb-queue-strip-icon">
          <i
            class="fa"
            :class="queueSummary.active ? 'fa-spinner fa-spin' : (queueSummary.failed ? 'fa-exclamation-triangle' : 'fa-check-circle')"
          ></i>
        </span>
        <div class="kb-queue-strip-body">
          <strong v-if="queueSummary.active">{{ queueSummary.active }} dokumen sedang diproses</strong>
          <strong v-else>Status pemrosesan Knowledge Base</strong>
          <span>
            {{ formatNumber(queueSummary.completed) }} dari {{ formatNumber(queueSummary.total) }} bagian selesai
            <template v-if="queueSummary.failed"> · {{ formatNumber(queueSummary.failed) }} bagian gagal</template>
          </span>
        </div>
        <small v-if="lastJobSyncLabel" class="kb-queue-strip-time">Diperbarui {{ lastJobSyncLabel }}</small>
      </div>
      <div v-if="jobPollError" class="kb-inline-alert is-warning">
        <i class="fa fa-exclamation-triangle"></i>
        <span>{{ jobPollError }}</span>
      </div>

      <div v-if="summaryError" class="kb-inline-alert is-warning">
        <i class="fa fa-exclamation-triangle"></i>
        <span>{{ summaryError }}</span>
        <button type="button" class="btn btn-sm kb-btn-ghost" @click="loadSummary">Coba lagi</button>
      </div>

      <div class="kb-search-card">
        <div class="kb-search-copy">
          <span class="kb-eyebrow">Pencarian Isi</span>
          <strong>Cari teks yang benar-benar tersimpan di Knowledge Base</strong>
          <small>Pencarian dilakukan di server pada lisensi yang dipilih, bukan di browser.</small>
        </div>
        <div class="kb-search-form">
          <i class="fa fa-search"></i>
          <input
            v-model.trim="kbSearchQuery"
            type="text"
            :disabled="!selectedLicense || kbSearchLoading"
            placeholder="Contoh: syarat pengiriman, harga, jam operasional..."
            @keyup.enter="runKbSearch"
          />
          <button type="button" class="btn kb-btn-primary" :disabled="!canSearch" @click="runKbSearch">
            <i v-if="kbSearchLoading" class="fa fa-spinner fa-spin"></i>
            <span v-else>Cari Isi</span>
          </button>
          <button v-if="searchActive" type="button" class="btn kb-btn-ghost" @click="clearSearch">
            Reset
          </button>
        </div>
        <div v-if="searchActive" class="kb-search-results">
          <div v-if="kbSearchError" class="kb-inline-alert is-danger">
            <i class="fa fa-exclamation-circle"></i>
            <span>{{ kbSearchError }}</span>
          </div>
          <div v-else-if="kbSearchLoading" class="kb-search-loading">
            <i class="fa fa-spinner fa-spin"></i> Mencari bagian teks...
          </div>
          <div v-else-if="!kbSearchResults.length" class="kb-search-empty">
            Tidak ada bagian teks yang cocok dengan kata pencarian.
          </div>
          <template v-else>
            <div class="kb-search-result-head">
              <span><strong>{{ kbSearchResults.length }}</strong> hasil ditemukan</span>
              <small v-if="kbSearchLimited">Pencarian diperiksa hingga 2.000 bagian pertama.</small>
            </div>
            <button
              v-for="hit in kbSearchResults"
              :key="hit.file_id + '-' + hit.chunk_index"
              type="button"
              class="kb-search-hit"
              @click="openSearchHit(hit)"
            >
              <span class="kb-search-hit-icon"><i class="fa fa-file-text-o"></i></span>
              <span class="kb-search-hit-body">
                <strong>{{ hit.file_name }}</strong>
                <small>{{ hit.preview || "Tidak ada cuplikan teks." }}</small>
              </span>
              <span class="kb-search-hit-meta">
                Bagian {{ hit.position || (hit.chunk_index + 1) }}
              </span>
            </button>
          </template>
        </div>
      </div>

      <div class="kb-workspace">
        <aside class="kb-doc-card">
          <div class="kb-panel-head">
            <div>
              <span class="kb-eyebrow">Dokumen</span>
              <h4>Daftar Dokumen</h4>
            </div>
            <span class="kb-count-pill">{{ filteredDocs.length }}</span>
          </div>

          <div class="kb-doc-filters">
            <div class="kb-input-wrap">
              <i class="fa fa-search"></i>
              <input v-model.trim="fileNameFilter" type="text" placeholder="Cari nama file..." />
            </div>
            <select v-model="statusFilter" class="form-control">
              <option value="">Semua status</option>
              <option value="stored">Tersimpan</option>
              <option value="processing">Sedang diproses</option>
              <option value="attention">Perlu diperiksa</option>
              <option value="failed">Gagal diproses</option>
              <option value="pending">Menunggu diproses</option>
            </select>
          </div>

          <div class="kb-doc-list">
            <template v-if="docsLoading">
              <div v-for="index in 3" :key="'doc-skeleton-' + index" class="kb-doc-skeleton">
                <span></span>
                <div><i></i><i></i></div>
              </div>
            </template>

            <div v-else-if="docsError" class="kb-state-box is-error">
              <i class="fa fa-exclamation-circle"></i>
              <strong>Dokumen belum berhasil dimuat</strong>
              <span>{{ docsError }}</span>
              <button type="button" class="btn kb-btn-ghost" @click="loadDocs">Coba lagi</button>
            </div>

            <div v-else-if="!docs.length" class="kb-state-box is-empty">
              <i class="fa fa-book"></i>
              <strong>Belum ada dokumen Knowledge Base untuk lisensi ini.</strong>
              <span>Unggah dokumen agar Chat AI dapat menggunakannya untuk menjawab pertanyaan pelanggan.</span>
              <button type="button" class="btn kb-btn-primary" @click="openUpload">
                <i class="fa fa-plus"></i> Upload Dokumen
              </button>
            </div>

            <div v-else-if="!filteredDocs.length" class="kb-state-box is-empty">
              <i class="fa fa-filter"></i>
              <strong>Tidak ada dokumen yang cocok</strong>
              <span>Ubah kata pencarian atau filter status.</span>
            </div>

            <template v-else>
              <button
                v-for="doc in filteredDocs"
                :key="doc.id"
                type="button"
                class="kb-doc-item"
                :class="{ 'is-active': selectedDoc && selectedDoc.id === doc.id }"
                @click="selectDoc(doc)"
              >
                <span class="kb-file-icon" :class="'is-' + fileKind(doc)">
                  <i :class="fileIcon(doc)"></i>
                </span>
                <span class="kb-doc-body">
                  <span class="kb-doc-title-row">
                    <strong :title="doc.file_name">{{ doc.file_name }}</strong>
                    <span class="kb-status-badge" :class="'is-' + docStatus(doc).tone">
                      {{ docStatus(doc).label }}
                    </span>
                  </span>
                  <span class="kb-doc-meta">
                    <span><i class="fa fa-clock-o"></i> {{ formatLocalDate(doc.create_datetime) || "Waktu tidak tersedia" }}</span>
                    <span><i class="fa fa-align-left"></i> {{ docChunkLabel(doc) }}</span>
                  </span>
                  <span v-if="docJob(doc)" class="kb-doc-queue">
                    <span class="kb-doc-queue-bar">
                      <span :class="'is-' + docJobMeta(doc).tone" :style="{ width: docJobPercent(doc) + '%' }"></span>
                    </span>
                    <em>
                      {{ docJobMeta(doc).label }} · {{ formatNumber(docJobCount(doc, "completed")) }} dari
                      {{ formatNumber(docJobCount(doc, "total")) }} bagian · {{ docJobPercentLabel(doc) }}
                    </em>
                  </span>
                  <small v-else-if="docAttention(doc)" class="kb-doc-attention">{{ docAttention(doc) }}</small>
                  <small v-if="docJob(doc) && docJobErrorText(doc)" class="kb-doc-attention">{{ docJobErrorText(doc) }}</small>
                  <small v-if="!docJob(doc) && docJobError[String(doc.id)]" class="kb-doc-attention">
                    {{ docJobError[String(doc.id)] }}
                  </small>
                </span>
                <span class="kb-doc-delete" :class="{ 'is-loading': deletingId === doc.id }" @click.stop="confirmDelete(doc)">
                  <i class="fa" :class="deletingId === doc.id ? 'fa-spinner fa-spin' : 'fa-trash-o'"></i>
                </span>
              </button>
            </template>
          </div>

          <div class="kb-doc-footer">
            <span><i class="fa fa-info-circle"></i> Maksimal 5 dokumen per lisensi.</span>
            <span v-if="isLimitReached" class="is-warning">Batas tercapai</span>
          </div>
        </aside>

        <section ref="detailCard" class="kb-detail-card">
          <div v-if="!selectedDoc" class="kb-detail-empty">
            <span class="kb-detail-empty-icon"><i class="fa fa-file-text-o"></i></span>
            <strong>Pilih dokumen untuk melihat isi Knowledge Base</strong>
            <span>Bagian teks yang ditampilkan adalah teks aktual yang digunakan Chat AI saat mencari jawaban.</span>
          </div>

          <template v-else>
            <div class="kb-detail-head">
              <div class="kb-detail-title">
                <span class="kb-file-icon is-large" :class="'is-' + fileKind(selectedDoc)">
                  <i :class="fileIcon(selectedDoc)"></i>
                </span>
                <div>
                  <span class="kb-eyebrow">Dokumen Knowledge Base</span>
                  <h4 :title="selectedDoc.file_name">{{ selectedDoc.file_name }}</h4>
                  <div class="kb-detail-meta">
                    <span><i class="fa fa-clock-o"></i> {{ formatLocalDate(selectedDoc.create_datetime) || "Waktu tidak tersedia" }}</span>
                    <span><i class="fa fa-align-left"></i> {{ docChunkLabel(selectedDoc) }}</span>
                    <span><i class="fa fa-file-o"></i> {{ fileTypeLabel(selectedDoc) }}</span>
                  </div>
                </div>
              </div>
              <div class="kb-detail-actions">
                <span class="kb-status-badge is-large" :class="'is-' + docStatus(selectedDoc).tone">
                  {{ docStatus(selectedDoc).label }}
                </span>
                <button
                  type="button"
                  class="btn kb-btn-ghost"
                  :disabled="!canEditSelectedDoc"
                  :title="selectedDocEditDisabledReason || 'Ubah seluruh isi dokumen Knowledge Base'"
                  @click="openEditor"
                >
                  <i class="fa fa-pencil"></i> Edit Isi
                </button>
                <a
                  v-if="safeFileUrl(selectedDoc.file_url)"
                  :href="selectedDoc.file_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn kb-btn-ghost"
                >
                  <i class="fa fa-external-link"></i> Lihat Dokumen Asli
                </a>
                <button
                  type="button"
                  class="btn kb-btn-danger-ghost"
                  :disabled="deletingId === selectedDoc.id"
                  @click="confirmDelete(selectedDoc)"
                >
                  <i class="fa" :class="deletingId === selectedDoc.id ? 'fa-spinner fa-spin' : 'fa-trash-o'"></i>
                  Hapus
                </button>
              </div>
            </div>

            <div v-if="selectedDocEditDisabledReason" class="kb-edit-hint">
              <i class="fa fa-info-circle"></i>
              {{ selectedDocEditDisabledReason }}
            </div>

            <div v-if="selectedDocJob" class="kb-job-card" :class="'is-' + selectedDocJobMeta.tone">
              <div class="kb-job-card-head">
                <div>
                  <span class="kb-eyebrow">Pemrosesan Knowledge Base</span>
                  <div class="kb-job-card-title">
                    <span class="kb-status-badge" :class="'is-' + selectedDocJobMeta.tone">
                      {{ selectedDocJobMeta.label }}
                    </span>
                    <strong>{{ selectedDocJobPercentLabel }} selesai</strong>
                  </div>
                  <small>
                    {{ formatNumber(selectedDocJob.progress.completed) }} /
                    {{ formatNumber(selectedDocJob.progress.total) }} bagian tersimpan
                    <template v-if="selectedDocJob.filename"> · {{ selectedDocJob.filename }}</template>
                  </small>
                </div>
                <small v-if="selectedDocJobUpdatedLabel" class="kb-job-card-time">
                  Diperbarui {{ selectedDocJobUpdatedLabel }}
                </small>
              </div>
              <div class="kb-job-progress">
                <span :class="'is-' + selectedDocJobMeta.tone" :style="{ width: selectedDocJobPercent + '%' }"></span>
              </div>
              <div class="kb-job-counts">
                <span><i class="kb-job-dot is-success"></i>Selesai <strong>{{ formatNumber(selectedDocJob.progress.completed) }}</strong></span>
                <span><i class="kb-job-dot is-processing"></i>Sedang proses <strong>{{ formatNumber(selectedDocJob.progress.processing) }}</strong></span>
                <span><i class="kb-job-dot is-muted"></i>Menunggu <strong>{{ formatNumber(selectedDocJob.progress.queued) }}</strong></span>
                <span><i class="kb-job-dot is-danger"></i>Gagal <strong>{{ formatNumber(selectedDocJob.progress.failed) }}</strong></span>
              </div>
              <p v-if="selectedDocJobError" class="kb-job-error">
                <i class="fa fa-exclamation-circle"></i> {{ selectedDocJobError }}
              </p>
            </div>

            <div class="kb-tabs">
              <button type="button" :class="{ active: activeTab === 'content' }" @click="activeTab = 'content'">
                <i class="fa fa-align-left"></i> Isi yang Tersimpan
              </button>
              <button type="button" :class="{ active: activeTab === 'info' }" @click="activeTab = 'info'">
                <i class="fa fa-info-circle"></i> Informasi
              </button>
            </div>

            <div v-if="activeTab === 'content'" class="kb-content-tab">
              <div v-if="selectedDocJob" class="kb-job-chunks">
                <div class="kb-job-chunks-head">
                  <div>
                    <span class="kb-eyebrow">Status Bagian Pemrosesan</span>
                    <strong>
                      {{ formatNumber(jobChunksTotal || selectedDocJob.progress.total) }} bagian
                      <template v-if="jobChunksTotalPages > 1">
                        · halaman {{ jobChunksPage }} dari {{ jobChunksTotalPages }}
                      </template>
                    </strong>
                  </div>
                  <button
                    type="button"
                    class="btn kb-btn-ghost"
                    :disabled="jobChunksLoading"
                    @click="loadJobChunks(jobChunksPage, true)"
                  >
                    <i class="fa" :class="jobChunksLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
                    Perbarui
                  </button>
                </div>
                <div v-if="jobChunksError" class="kb-inline-alert is-danger">
                  <i class="fa fa-exclamation-circle"></i>
                  <span>{{ jobChunksError }}</span>
                  <button type="button" class="btn btn-sm kb-btn-ghost" @click="loadJobChunks(jobChunksPage, true)">
                    Coba lagi
                  </button>
                </div>
                <div v-else-if="jobChunksLoading && !jobChunks.length" class="kb-job-chunks-loading">
                  <i class="fa fa-spinner fa-spin"></i> Memuat status bagian pemrosesan...
                </div>
                <div v-else-if="!jobChunks.length" class="kb-job-chunks-empty">
                  Status bagian belum tersedia. Perbarui beberapa saat lagi.
                </div>
                <div v-else class="kb-job-chunk-list">
                  <div
                    v-for="chunk in jobChunks"
                    :key="chunk.chunk_index"
                    class="kb-job-chunk"
                    :class="'is-' + jobChunkMeta(chunk).tone"
                  >
                    <span class="kb-chunk-number">{{ chunk.sequence }}</span>
                    <div class="kb-job-chunk-body">
                      <div class="kb-job-chunk-head">
                        <strong>Bagian {{ chunk.sequence }}</strong>
                        <span class="kb-status-badge" :class="'is-' + jobChunkMeta(chunk).tone">
                          {{ jobChunkMeta(chunk).label }}
                        </span>
                      </div>
                      <small v-if="chunk.preview" class="kb-job-chunk-preview">{{ chunk.preview }}</small>
                      <em>
                        {{ formatNumber(chunk.char_count) }} karakter ·
                        {{ formatNumber(chunk.attempts) }} percobaan
                      </em>
                      <p v-if="jobChunkErrorText(chunk)" class="kb-job-chunk-error">
                        <i class="fa fa-exclamation-triangle"></i> {{ jobChunkErrorText(chunk) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div v-if="jobChunksTotalPages > 1" class="kb-job-chunk-pager">
                  <button
                    type="button"
                    class="btn kb-btn-ghost"
                    :disabled="jobChunksLoading || jobChunksPage <= 1"
                    @click="changeJobChunkPage(jobChunksPage - 1)"
                  >
                    <i class="fa fa-chevron-left"></i> Sebelumnya
                  </button>
                  <span>Halaman {{ jobChunksPage }} / {{ jobChunksTotalPages }}</span>
                  <button
                    type="button"
                    class="btn kb-btn-ghost"
                    :disabled="jobChunksLoading || jobChunksPage >= jobChunksTotalPages"
                    @click="changeJobChunkPage(jobChunksPage + 1)"
                  >
                    Berikutnya <i class="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>

              <div v-if="chunksLoading && !chunks.length" class="kb-content-loading">
                <i class="fa fa-spinner fa-spin"></i> Memuat bagian teks dari Knowledge Base...
              </div>
              <div v-else-if="chunksError" class="kb-state-box is-error">
                <i class="fa fa-exclamation-circle"></i>
                <strong>Bagian teks belum berhasil dimuat</strong>
                <span>{{ chunksError }}</span>
                <button type="button" class="btn kb-btn-ghost" @click="loadChunks(true)">Coba lagi</button>
              </div>
              <div v-else-if="!chunks.length" class="kb-state-box is-empty">
                <i class="fa fa-file-o"></i>
                <strong>Belum ada bagian teks yang tersimpan</strong>
                <span v-if="selectedDocJob && selectedDocJobIsActive">
                  Bagian teks akan tersedia di sini setelah pemrosesan Knowledge Base selesai.
                </span>
                <span v-else>Dokumen mungkin masih diproses atau pemrosesan belum berhasil. Muat ulang beberapa saat lagi.</span>
                <button type="button" class="btn kb-btn-ghost" @click="refreshAll">Muat ulang</button>
              </div>
              <div v-else class="kb-content-grid">
                <div class="kb-chunk-panel">
                  <div class="kb-subhead">
                    <div>
                      <span class="kb-eyebrow">Daftar Bagian</span>
                      <strong>{{ totalChunks }} bagian teks</strong>
                    </div>
                  </div>
                  <div class="kb-chunk-list">
                    <button
                      v-for="chunk in chunks"
                      :key="chunk.chunk_index"
                      type="button"
                      class="kb-chunk-item"
                      :class="{ 'is-active': selectedChunk && selectedChunk.chunk_index === chunk.chunk_index }"
                      @click="selectChunk(chunk)"
                    >
                      <span class="kb-chunk-number">{{ chunk.position }}</span>
                      <span class="kb-chunk-body">
                        <strong>Bagian {{ chunk.position }}</strong>
                        <small>{{ chunk.preview || "Tidak ada cuplikan teks." }}</small>
                        <em>{{ formatNumber(chunk.char_count) }} karakter</em>
                      </span>
                    </button>
                  </div>
                  <button v-if="hasMore" type="button" class="btn kb-btn-ghost kb-load-more" :disabled="chunksLoading" @click="loadMoreChunks">
                    <i class="fa" :class="chunksLoading ? 'fa-spinner fa-spin' : 'fa-chevron-down'"></i>
                    Muat bagian berikutnya
                  </button>
                </div>

                <div class="kb-preview-panel">
                  <div class="kb-preview-head">
                    <div>
                      <span class="kb-eyebrow">Preview Teks Tersimpan</span>
                      <strong v-if="selectedChunk">Bagian {{ selectedChunk.position }} dari {{ totalChunks }}</strong>
                      <strong v-else>Pilih salah satu bagian</strong>
                    </div>
                    <div class="kb-preview-nav">
                      <button type="button" class="btn kb-icon-btn" :disabled="!canSelectPrev" @click="selectPrevChunk">
                        <i class="fa fa-chevron-left"></i>
                      </button>
                      <button type="button" class="btn kb-icon-btn" :disabled="!canSelectNext" @click="selectNextChunk">
                        <i class="fa fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                  <div class="kb-preview-note">
                    <i class="fa fa-info-circle"></i>
                    Bagian teks ini merupakan salah satu bagian dokumen yang digunakan Chat AI untuk mencari informasi ketika menjawab pertanyaan pelanggan.
                  </div>
                  <div v-if="chunkContentLoading" class="kb-preview-loading">
                    <i class="fa fa-spinner fa-spin"></i> Memuat teks...
                  </div>
                  <div v-else-if="chunkContentError" class="kb-state-box is-error is-compact">
                    <i class="fa fa-exclamation-circle"></i>
                    <strong>Teks belum berhasil dimuat</strong>
                    <span>{{ chunkContentError }}</span>
                    <button type="button" class="btn kb-btn-ghost" @click="loadSelectedChunk">Coba lagi</button>
                  </div>
                  <div v-else class="kb-preview-text">{{ chunkContent }}</div>
                </div>
              </div>
            </div>

            <div v-else class="kb-info-tab">
              <div class="kb-info-grid">
                <div class="kb-info-row">
                  <span>Nama file</span>
                  <strong>{{ selectedDoc.file_name || "—" }}</strong>
                </div>
                <div class="kb-info-row">
                  <span>Jenis file</span>
                  <strong>{{ fileTypeLabel(selectedDoc) }}</strong>
                </div>
                <div class="kb-info-row">
                  <span>Waktu upload</span>
                  <strong>{{ formatLocalDate(selectedDoc.create_datetime) || "—" }}</strong>
                </div>
                <div class="kb-info-row">
                  <span>Lisensi WhatsApp</span>
                  <strong>{{ selectedLicense.license_name || "—" }} ({{ selectedLicense.license_phone_number || "-" }})</strong>
                </div>
                <div class="kb-info-row">
                  <span>Jumlah bagian teks</span>
                  <strong>{{ docChunkLabel(selectedDoc) }}</strong>
                </div>
                <div class="kb-info-row">
                  <span>Status pemrosesan</span>
                  <strong :class="'is-' + docStatus(selectedDoc).tone">{{ docStatus(selectedDoc).label }}</strong>
                </div>
                <div class="kb-info-row">
                  <span>Proses antrean</span>
                  <strong v-if="selectedDocJob">
                    {{ selectedDocJobMeta.label }} · {{ formatNumber(selectedDocJob.progress.completed) }} /
                    {{ formatNumber(selectedDocJob.progress.total) }} bagian
                  </strong>
                  <strong v-else class="is-muted">Tidak ada data antrean (dokumen lama)</strong>
                </div>
                <div class="kb-info-row">
                  <span>Tersedia untuk Chat AI</span>
                  <strong :class="docSummary && docSummary.ready ? 'is-success' : 'is-warning'">
                    {{ docSummary ? (docSummary.ready ? "Ya, bagian teks tersedia" : "Belum siap digunakan") : "Belum dapat diverifikasi" }}
                  </strong>
                </div>
                <div v-if="docSummary && docSummary.expected_chunks > 0" class="kb-info-row">
                  <span>Perkiraan total bagian</span>
                  <strong>{{ formatNumber(docSummary.expected_chunks) }} bagian</strong>
                </div>
              </div>
              <div class="kb-info-note">
                <span class="kb-eyebrow">Catatan Pemrosesan</span>
                <p>{{ docSummary ? docSummary.safe_message : "Informasi pemrosesan belum tersedia. Muat ulang halaman untuk memeriksa kembali." }}</p>
                <small v-if="docSummary && docSummary.needs_attention">
                  Dokumen ini ditandai perlu diperiksa. Chat AI hanya dapat memakai bagian teks yang benar-benar tersimpan.
                </small>
              </div>
              <div v-if="isSelectedDocLegacy" class="kb-info-note is-muted">
                <span class="kb-eyebrow">Catatan Antrean</span>
                <p>
                  Dokumen ini dibuat sebelum monitoring antrean Knowledge Base tersedia, sehingga tidak memiliki data proses.
                  Isi Knowledge Base tetap dapat diperiksa seperti biasa.
                </p>
              </div>
              <div class="kb-info-note is-muted">
                <span class="kb-eyebrow">Catatan Teknis</span>
                <p>Halaman ini hanya menampilkan teks yang tersimpan. Data vektor dan detail internal penyimpanan tidak ditampilkan.</p>
              </div>
            </div>
          </template>
        </section>
      </div>
    </div>

    <form-data
      v-if="showUploadModal"
      :initial-license="selectedLicense"
      @close="showUploadModal = false"
      @uploaded="onUploaded"
    />

    <knowledge-base-editor-modal
      :visible="showEditorModal"
      :doc="editorDoc"
      :job="editorJob"
      :job-loading="selectedDocJobLoading"
      :license-id="selectedLicense ? selectedLicense.id : ''"
      @close="closeEditor"
      @content-loaded="onEditorContentLoaded"
      @job-created="onEditorJobCreated"
      @job-success="onEditorJobSuccess"
      @job-failed="onEditorJobFailed"
    />
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import FormData from "./historykbAdd.vue";
import KnowledgeBaseEditorModal from "./components/KnowledgeBaseEditorModal.vue";
import { formatLocalDate } from "@/utils/helper";
import {
  createKbJobMonitor,
  formatKbPercent,
  isKbJobActive,
  kbChunkStatusMeta,
  kbErrorHttpStatus,
  kbJobStatusMeta,
  normalizeKbJob,
  safeKbChunkError,
  safeKbJobError,
  shouldApplyKbJob,
} from "@/utils/knowledgeBaseQueue";
import {
  kbEditorCanEdit,
  kbEditorContentIsProcessing,
  kbEditorEditDisabledReason,
} from "@/utils/knowledgeBaseEditor";

require("vue-multiselect/dist/vue-multiselect.min.css");

export default {
  name: "historykb",
  components: {
    Multiselect,
    FormData,
    KnowledgeBaseEditorModal,
  },
  data() {
    return {
      licenseOptions: [],
      selectedLicense: null,
      licenseLoading: false,

      docs: [],
      docsLoading: false,
      docsError: "",
      docsRequestId: 0,

      summary: null,
      summaryLoading: false,
      summaryError: "",
      summaryRequestId: 0,

      fileNameFilter: "",
      statusFilter: "",
      selectedDoc: null,
      activeTab: "content",

      chunks: [],
      chunksLoading: false,
      chunksError: "",
      chunksRequestId: 0,
      totalChunks: 0,
      hasMore: false,
      nextAfterIndex: -1,
      nextOffset: 0,
      selectedChunk: null,

      chunkContent: "",
      chunkContentLoading: false,
      chunkContentError: "",
      chunkDetailRequestId: 0,
      chunkCache: {},

      // Monitoring antrean pemrosesan Knowledge Base.
      docJobs: {},
      docJobLoading: {},
      docJobMissing: {},
      docJobError: {},
      jobPollError: "",
      lastJobSyncAt: 0,

      jobChunks: [],
      jobChunksLoading: false,
      jobChunksError: "",
      jobChunksPage: 1,
      jobChunksLimit: 25,
      jobChunksTotal: 0,
      jobChunksTotalPages: 0,
      jobChunksJobId: "",
      jobChunksRequestId: 0,

      kbSearchQuery: "",
      kbSearchLoading: false,
      kbSearchError: "",
      kbSearchResults: [],
      kbSearchLimited: false,
      searchActive: false,

      deletingId: 0,
      showUploadModal: false,
      showEditorModal: false,
      editorDoc: null,
    };
  },
  computed: {
    summaryMap() {
      const map = {};
      if (this.summary && this.summary.files) {
        this.summary.files.forEach((file) => {
          map[String(file.file_id)] = file;
        });
      }
      return map;
    },
    docSummary() {
      if (!this.selectedDoc) return null;
      return this.summaryMap[String(this.selectedDoc.id)] || null;
    },
    filteredDocs() {
      const keyword = (this.fileNameFilter || "").toLowerCase();
      return this.docs.filter((doc) => {
        const matchesName = !keyword || String(doc.file_name || "").toLowerCase().indexOf(keyword) !== -1;
        const matchesStatus = !this.statusFilter || this.docStatusKey(doc) === this.statusFilter;
        return matchesName && matchesStatus;
      });
    },
    isLimitReached() {
      return this.docs.length >= 5;
    },
    canSearch() {
      return !!this.selectedLicense && !this.kbSearchLoading && this.kbSearchQuery.trim().length >= 2;
    },
    selectedChunkPosition() {
      if (!this.selectedChunk) return 0;
      return this.selectedChunk.position || (this.chunks.findIndex((chunk) => chunk.chunk_index === this.selectedChunk.chunk_index) + 1);
    },
    selectedChunkListIndex() {
      if (!this.selectedChunk) return -1;
      return this.chunks.findIndex((chunk) => chunk.chunk_index === this.selectedChunk.chunk_index);
    },
    canSelectPrev() {
      return this.selectedChunkListIndex > 0;
    },
    canSelectNext() {
      if (this.selectedChunkListIndex < 0) return false;
      return this.selectedChunkListIndex < this.chunks.length - 1 || this.hasMore;
    },
    selectedDocJob() {
      return this.selectedDoc ? this.docJob(this.selectedDoc) : null;
    },
    selectedDocJobMeta() {
      return kbJobStatusMeta(this.selectedDocJob && this.selectedDocJob.status);
    },
    selectedDocJobPercent() {
      return this.selectedDocJob ? Number(this.selectedDocJob.progress.percent) || 0 : 0;
    },
    selectedDocJobPercentLabel() {
      return formatKbPercent(this.selectedDocJobPercent);
    },
    selectedDocJobError() {
      return safeKbJobError(this.selectedDocJob);
    },
    selectedDocJobUpdatedLabel() {
      return this.jobClock(this.selectedDocJob && this.selectedDocJob.updated_at);
    },
    selectedDocJobIsActive() {
      return !!this.selectedDocJob && isKbJobActive(this.selectedDocJob.status);
    },
    selectedDocJobLoading() {
      if (!this.selectedDoc) return false;
      return !!this.docJobLoading[String(this.selectedDoc.id)];
    },
    editorJob() {
      if (!this.editorDoc) return null;
      return this.docJobs[String(this.editorDoc.id)] || null;
    },
    selectedDocEditDisabledReason() {
      if (!this.selectedDoc) return "";
      return kbEditorEditDisabledReason(
        this.selectedDocJob,
        this.docStatusKey(this.selectedDoc)
      );
    },
    canEditSelectedDoc() {
      if (!this.selectedDoc || this.deletingId) return false;
      return kbEditorCanEdit(this.selectedDocJob, this.docStatusKey(this.selectedDoc));
    },
    isSelectedDocLegacy() {
      if (!this.selectedDoc || this.selectedDocJob) return false;
      return !!this.docJobMissing[String(this.selectedDoc.id)];
    },
    queueSummary() {
      const keys = Object.keys(this.docJobs);
      let completed = 0;
      let total = 0;
      let failed = 0;
      let active = 0;
      keys.forEach((key) => {
        const job = this.docJobs[key];
        if (!job) return;
        completed += job.progress.completed;
        total += job.progress.total;
        failed += job.progress.failed;
        if (isKbJobActive(job.status)) active += 1;
      });
      return { documents: keys.length, active: active, completed: completed, total: total, failed: failed };
    },
    lastJobSyncLabel() {
      if (!this.lastJobSyncAt) return "";
      return this.jobClock(this.lastJobSyncAt * 1000);
    },
  },
  created() {
    this.jobMonitor = createKbJobMonitor({
      intervalMs: 3000,
      fetchJob: (jobId) => this.$store.dispatch("aiKey/knowledgeBaseJobDetail", { job_id: jobId }),
      onUpdate: (job) => this.applyJobUpdate(job),
      onError: () => {
        this.jobPollError = "Pembaruan status antrean tertunda. Sistem mencoba lagi secara otomatis.";
      },
    });
    this.summaryRefreshTimer = null;
    this.jobsSuspended = false;
    this.loadLicenses();
  },
  beforeDestroy() {
    this.jobsSuspended = true;
    this.closeEditor();
    if (this.jobMonitor) this.jobMonitor.dispose();
    this.clearSummaryRefreshTimer();
  },
  deactivated() {
    this.showUploadModal = false;
    this.closeEditor();
    this.jobsSuspended = true;
    this.stopJobMonitoring();
    this.clearSummaryRefreshTimer();
  },
  activated() {
    this.jobsSuspended = false;
    if (this.selectedLicense) this.refreshDocJobs();
  },
  methods: {
    formatLocalDate(value) {
      const timestamp = Number(value);
      if (!timestamp || timestamp <= 0) return "";
      return formatLocalDate(timestamp);
    },
    formatNumber(value) {
      const number = Number(value);
      if (!isFinite(number)) return "0";
      return String(Math.round(number)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    limitText(count) {
      return `dan ${count} lainnya`;
    },
    customLabel(option) {
      if (!option) return "";
      return `${option.license_name || "Tanpa nama"} - ${option.license_phone_number || "-"}`;
    },
    summaryValue(value) {
      if (this.summaryLoading) return "…";
      if (this.summaryError || value === null || value === undefined) return "—";
      return this.formatNumber(value);
    },
    errorMessage(error, fallback) {
      if (!error) return fallback;
      const message = error.message || error.msg || "";
      if (!message) return fallback;
      return message.replace(/<br\s*\/?>/gi, " ");
    },
    jobClock(value) {
      if (!value) return "";
      const date = typeof value === "number" ? new Date(value) : new Date(String(value));
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    },
    docJob(doc) {
      if (!doc) return null;
      return this.docJobs[String(doc.id)] || null;
    },
    docJobMeta(doc) {
      const job = this.docJob(doc);
      return kbJobStatusMeta(job && job.status);
    },
    docJobPercent(doc) {
      const job = this.docJob(doc);
      return job ? Number(job.progress.percent) || 0 : 0;
    },
    docJobPercentLabel(doc) {
      return formatKbPercent(this.docJobPercent(doc));
    },
    docJobCount(doc, key) {
      const job = this.docJob(doc);
      if (!job) return 0;
      return Number(job.progress[key]) || 0;
    },
    docJobErrorText(doc) {
      const job = this.docJob(doc);
      return safeKbJobError(job);
    },
    jobChunkMeta(chunk) {
      return kbChunkStatusMeta(chunk && chunk.status);
    },
    jobChunkErrorText(chunk) {
      return safeKbChunkError(chunk);
    },
    applyJob(job, options) {
      if (!job || !job.file_id) return false;
      const force = !!(options && options.force);
      const key = String(job.file_id);
      const previous = this.docJobs[key] || null;
      if (!force && !shouldApplyKbJob(previous, job)) return false;
      if (previous && previous.job_id && previous.job_id !== job.job_id) {
        this.jobMonitor.untrack(previous.job_id);
      }
      this.$set(this.docJobs, key, job);
      this.$set(this.docJobMissing, key, false);
      this.$set(this.docJobError, key, "");
      this.lastJobSyncAt = Math.floor(Date.now() / 1000);
      if (this.selectedDoc && Number(this.selectedDoc.id) === Number(job.file_id)) {
        this.ensureJobChunks();
      }
      return true;
    },
    applyJobUpdate(job) {
      if (!job) return;
      this.jobPollError = "";
      const key = String(job.file_id);
      const previous = this.docJobs[key] || null;
      const applied = this.applyJob(job);
      if (!applied) return;
      if (previous && isKbJobActive(previous.status) && !isKbJobActive(job.status)) {
        this.onJobFinished(job);
      }
    },
    onJobFinished(job) {
      this.scheduleSummaryRefresh();
      const isSelected = this.selectedDoc && Number(this.selectedDoc.id) === Number(job.file_id);
      if (!isSelected) return;
      this.loadJobChunks(1, true);
      if (job.status === "success") {
        this.loadChunks(true);
      }
    },
    async resolveDocJob(doc) {
      if (!doc || !this.selectedLicense) return;
      const key = String(doc.id);
      if (this.docJobLoading[key]) return;
      const licenseId = this.selectedLicense.id;
      this.$set(this.docJobLoading, key, true);
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseLatestJob", {
          file_id: doc.id,
          license_id: licenseId,
        });
        if (this.jobsSuspended) return;
        if (!this.selectedLicense || String(this.selectedLicense.id) !== String(licenseId)) return;
        const job = normalizeKbJob(response.data);
        if (!job || !job.file_id) return;
        this.applyJob(job);
        if (typeof console !== "undefined" && console.info) {
          console.info("[kb-editor] latest job", {
            file_id: doc.id,
            job_id: job.job_id,
            status: job.status,
            content_version: job.content_version,
          });
        }
        if (isKbJobActive(job.status)) {
          this.jobMonitor.track(job.job_id);
        }
      } catch (error) {
        if (this.jobsSuspended) return;
        if (!this.selectedLicense || String(this.selectedLicense.id) !== String(licenseId)) return;
        const status = kbErrorHttpStatus(error);
        if (typeof console !== "undefined" && console.info) {
          console.info("[kb-editor] latest job", { file_id: doc.id, status: status });
        }
        if (status === 404) {
          // Dokumen legacy/new tanpa job: bukan kegagalan, dan cache job aktif
          // lama tidak boleh terus menonaktifkan editor.
          this.clearStaleActiveDocJob(doc);
          this.$set(this.docJobMissing, key, true);
          this.$set(this.docJobError, key, "");
        } else {
          this.$set(this.docJobError, key, "Status antrean belum dapat diperiksa.");
        }
      } finally {
        this.$set(this.docJobLoading, key, false);
      }
    },
    clearStaleActiveDocJob(doc) {
      if (!doc) return;
      const key = String(doc.id);
      const existing = this.docJobs[key];
      if (!existing || !isKbJobActive(existing.status)) return;
      if (this.jobMonitor) this.jobMonitor.untrack(existing.job_id);
      this.$delete(this.docJobs, key);
    },
    refreshDocJobs() {
      this.docs.forEach((doc) => {
        this.resolveDocJob(doc);
      });
    },
    stopJobMonitoring() {
      if (this.jobMonitor) this.jobMonitor.untrackAll();
    },
    resetJobState() {
      this.stopJobMonitoring();
      this.docJobs = {};
      this.docJobLoading = {};
      this.docJobMissing = {};
      this.docJobError = {};
      this.jobPollError = "";
      this.lastJobSyncAt = 0;
      this.resetJobChunks();
    },
    scheduleSummaryRefresh() {
      this.clearSummaryRefreshTimer();
      this.summaryRefreshTimer = setTimeout(() => {
        this.summaryRefreshTimer = null;
        this.loadSummary();
      }, 600);
    },
    clearSummaryRefreshTimer() {
      if (this.summaryRefreshTimer) {
        clearTimeout(this.summaryRefreshTimer);
        this.summaryRefreshTimer = null;
      }
    },
    resetJobChunks() {
      this.jobChunks = [];
      this.jobChunksError = "";
      this.jobChunksPage = 1;
      this.jobChunksTotal = 0;
      this.jobChunksTotalPages = 0;
      this.jobChunksJobId = "";
      this.jobChunksLoading = false;
      this.jobChunksRequestId++;
    },
    ensureJobChunks() {
      const job = this.selectedDocJob;
      if (!job || !job.job_id) return;
      if (this.jobChunksJobId === job.job_id) return;
      this.loadJobChunks(1);
    },
    async loadJobChunks(page, force) {
      const job = this.selectedDocJob;
      if (!job || !job.job_id || this.jobChunksLoading) return;
      const targetPage = Math.max(Number(page) || 1, 1);
      if (!force && targetPage === this.jobChunksPage && this.jobChunks.length) return;
      const jobId = job.job_id;
      const requestId = ++this.jobChunksRequestId;
      this.jobChunksLoading = true;
      this.jobChunksError = "";
      this.jobChunksJobId = jobId;
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseJobChunks", {
          job_id: jobId,
          page: targetPage,
          limit: this.jobChunksLimit,
        });
        if (requestId !== this.jobChunksRequestId) return;
        if (!this.selectedDocJob || this.selectedDocJob.job_id !== jobId) return;
        const data = response.data || {};
        const pagination = data.pagination || {};
        this.jobChunks = data.items || [];
        this.jobChunksPage = Number(pagination.page) || targetPage;
        this.jobChunksTotal = Number(pagination.total) || 0;
        this.jobChunksTotalPages = Number(pagination.total_pages) || 0;
      } catch (error) {
        if (requestId !== this.jobChunksRequestId) return;
        this.jobChunks = [];
        this.jobChunksTotal = 0;
        this.jobChunksTotalPages = 0;
        this.jobChunksError = this.errorMessage(error, "Status bagian pemrosesan belum berhasil dimuat.");
      } finally {
        if (requestId === this.jobChunksRequestId) {
          this.jobChunksLoading = false;
        }
      }
    },
    changeJobChunkPage(page) {
      const target = Number(page) || 1;
      if (target < 1 || (this.jobChunksTotalPages && target > this.jobChunksTotalPages)) return;
      this.loadJobChunks(target, true);
    },
    async searchLicenses(query) {
      this.licenseLoading = true;
      try {
        const response = await this.$store.dispatch("license/getLicenseList", {
          license_name: query || "",
          license_phone_number: "",
          license_status: "",
          page: 0,
          perPage: 200,
        });
        this.licenseOptions = (response.data && response.data.data) || [];
      } catch (error) {
        this.licenseOptions = [];
      } finally {
        this.licenseLoading = false;
      }
    },
    async loadLicenses() {
      this.licenseLoading = true;
      try {
        const response = await this.$store.dispatch("license/getLicenseList", {
          license_name: "",
          license_phone_number: "",
          license_status: "",
          page: 0,
          perPage: 200,
        });
        this.licenseOptions = (response.data && response.data.data) || [];
        const routeLicenseId = this.$route && this.$route.query ? this.$route.query.license_id : "";
        let target = null;
        if (routeLicenseId) {
          target = this.licenseOptions.find((item) => String(item.id) === String(routeLicenseId));
        }
        if (!target && this.licenseOptions.length) {
          target = this.licenseOptions[0];
        }
        this.selectedLicense = target || null;
        if (target) {
          await this.loadDocs();
        }
      } catch (error) {
        this.licenseOptions = [];
        this.selectedLicense = null;
      } finally {
        this.licenseLoading = false;
      }
    },
    async onLicenseChange(value) {
      if (value !== undefined) {
        this.selectedLicense = value;
      }
      this.selectedDoc = null;
      this.docs = [];
      this.summary = null;
      this.summaryError = "";
      this.fileNameFilter = "";
      this.statusFilter = "";
      this.resetChunkState();
      this.resetJobState();
      this.clearSearch();
      if (this.selectedLicense) {
        await this.loadDocs();
      }
    },
    normalizeDoc(row) {
      return Object.assign({}, row, {
        id: Number(row.id),
      });
    },
    async loadDocs(options) {
      if (!this.selectedLicense) {
        this.docs = [];
        this.selectedDoc = null;
        this.summary = null;
        this.resetChunkState();
        this.resetJobState();
        return;
      }
      const previousSelectedId = options && options.preserveSelection && this.selectedDoc ? this.selectedDoc.id : null;
      const requestId = ++this.docsRequestId;
      this.docsLoading = true;
      this.docsError = "";
      try {
        const response = await this.$store.dispatch("aiKey/historyListKB", {
          page: 0,
          perPage: 50,
          columnFilters: { license_id: this.selectedLicense.id },
        });
        if (requestId !== this.docsRequestId) return;
        const rows = (response.data && response.data.data) || [];
        this.docs = rows
          .filter((row) => String(row.license_id) === String(this.selectedLicense.id))
          .map(this.normalizeDoc);
        this.docsLoading = false;
        this.refreshDocJobs();
        await this.loadSummary();
        if (requestId !== this.docsRequestId) return;
        let target = null;
        if (previousSelectedId) {
          target = this.docs.find((doc) => doc.id === previousSelectedId) || null;
        }
        if (!target && this.docs.length) {
          target = this.docs[0];
        }
        if (target) {
          await this.selectDoc(target, true);
        } else {
          this.selectedDoc = null;
          this.resetChunkState();
        }
      } catch (error) {
        if (requestId !== this.docsRequestId) return;
        this.docs = [];
        this.selectedDoc = null;
        this.summary = null;
        this.summaryError = "";
        this.docsError = this.errorMessage(error, "Dokumen Knowledge Base belum berhasil dimuat.");
        this.resetChunkState();
      } finally {
        if (requestId === this.docsRequestId) {
          this.docsLoading = false;
        }
      }
    },
    async loadSummary() {
      if (!this.selectedLicense) {
        this.summary = null;
        this.summaryError = "";
        return;
      }
      const requestId = ++this.summaryRequestId;
      this.summaryLoading = true;
      this.summaryError = "";
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseSummary", {
          license_id: this.selectedLicense.id,
        });
        if (requestId !== this.summaryRequestId) return;
        this.summary = response.data;
      } catch (error) {
        if (requestId !== this.summaryRequestId) return;
        this.summary = null;
        this.summaryError = this.errorMessage(
          error,
          "Ringkasan isi Knowledge Base belum berhasil dimuat. Angka ringkasan disembunyikan sampai data dapat diverifikasi."
        );
      } finally {
        if (requestId === this.summaryRequestId) {
          this.summaryLoading = false;
        }
      }
    },
    async selectDoc(doc, force) {
      if (!doc) return;
      const changed = !this.selectedDoc || this.selectedDoc.id !== doc.id;
      this.selectedDoc = doc;
      // Segarkan status job dokumen terpilih agar cache job aktif yang sudah
      // selesai/404 tidak menonaktifkan tombol Edit Isi.
      this.resolveDocJob(doc);
      if (changed || force) {
        this.activeTab = "content";
        this.resetChunkState();
        this.resetJobChunks();
        await this.loadChunks(true);
        this.ensureJobChunks();
      }
    },
    resetChunkState() {
      this.chunks = [];
      this.chunksError = "";
      this.totalChunks = 0;
      this.hasMore = false;
      this.nextAfterIndex = -1;
      this.nextOffset = 0;
      this.selectedChunk = null;
      this.chunkContent = "";
      this.chunkContentError = "";
      this.chunkContentLoading = false;
      this.chunkCache = {};
      this.chunksRequestId++;
    },
    async loadChunks(reset, options) {
      if (!this.selectedDoc || !this.selectedLicense) return;
      const requestId = ++this.chunksRequestId;
      const opts = options || {};
      const afterIndex = opts.afterIndex !== undefined ? opts.afterIndex : (reset ? -1 : this.nextAfterIndex);
      const offset = opts.offset !== undefined ? opts.offset : (reset ? 0 : this.nextOffset);
      if (reset) {
        this.chunks = [];
        this.chunksError = "";
        this.totalChunks = 0;
        this.hasMore = false;
        this.selectedChunk = null;
        this.chunkContent = "";
        this.chunkContentError = "";
        this.chunkCache = {};
      }
      this.chunksLoading = true;
      this.chunksError = "";
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseChunks", {
          license_id: this.selectedLicense.id,
          file_id: this.selectedDoc.id,
          after_index: afterIndex,
          offset: offset,
          limit: 25,
        });
        if (requestId !== this.chunksRequestId) return;
        const data = response.data || {};
        const incoming = data.chunks || [];
        this.chunks = reset ? incoming : this.chunks.concat(incoming);
        this.totalChunks = Number(data.total) || 0;
        this.hasMore = !!data.has_more;
        this.nextAfterIndex = Number(data.next_after_index);
        this.nextOffset = Number(data.next_offset) || 0;
        const targetIndex = opts.selectChunkIndex;
        if (targetIndex !== undefined) {
          const target = this.chunks.find((chunk) => chunk.chunk_index === targetIndex);
          if (target) {
            this.selectChunk(target);
          } else if (this.chunks.length) {
            this.selectChunk(this.chunks[0]);
          }
        } else if (this.selectedChunk) {
          const stillExists = this.chunks.find((chunk) => chunk.chunk_index === this.selectedChunk.chunk_index);
          if (!stillExists && this.chunks.length) this.selectChunk(this.chunks[0]);
        } else if (this.chunks.length) {
          this.selectChunk(this.chunks[0]);
        }
      } catch (error) {
        if (requestId !== this.chunksRequestId) return;
        this.chunksError = this.errorMessage(error, "Bagian teks belum berhasil dimuat.");
      } finally {
        if (requestId === this.chunksRequestId) {
          this.chunksLoading = false;
        }
      }
    },
    async loadMoreChunks() {
      if (this.chunksLoading || !this.hasMore) return;
      await this.loadChunks(false);
    },
    chunkCacheKey(chunk) {
      return String(this.selectedDoc ? this.selectedDoc.id : "") + ":" + String(chunk.chunk_index);
    },
    selectChunk(chunk) {
      if (!chunk) return;
      this.selectedChunk = chunk;
      this.activeTab = "content";
      const key = this.chunkCacheKey(chunk);
      if (this.chunkCache[key] !== undefined) {
        this.chunkContent = this.chunkCache[key];
        this.chunkContentError = "";
        this.chunkContentLoading = false;
        return;
      }
      this.loadSelectedChunk();
    },
    async loadSelectedChunk() {
      if (!this.selectedDoc || !this.selectedLicense || !this.selectedChunk) return;
      const chunk = this.selectedChunk;
      const requestId = ++this.chunkDetailRequestId;
      this.chunkContentLoading = true;
      this.chunkContentError = "";
      this.chunkContent = "";
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseChunkDetail", {
          license_id: this.selectedLicense.id,
          file_id: this.selectedDoc.id,
          chunk_index: chunk.chunk_index,
        });
        if (requestId !== this.chunkDetailRequestId) return;
        const data = response.data || {};
        this.chunkContent = data.content || "";
        this.chunkCache[this.chunkCacheKey(chunk)] = this.chunkContent;
      } catch (error) {
        if (requestId !== this.chunkDetailRequestId) return;
        this.chunkContentError = this.errorMessage(error, "Teks bagian ini belum berhasil dimuat.");
      } finally {
        if (requestId === this.chunkDetailRequestId) {
          this.chunkContentLoading = false;
        }
      }
    },
    selectPrevChunk() {
      if (!this.canSelectPrev) return;
      this.selectChunk(this.chunks[this.selectedChunkListIndex - 1]);
    },
    async selectNextChunk() {
      if (!this.selectedChunk) return;
      const nextIndex = this.selectedChunkListIndex + 1;
      if (nextIndex < this.chunks.length) {
        this.selectChunk(this.chunks[nextIndex]);
        return;
      }
      if (this.hasMore) {
        const beforeLength = this.chunks.length;
        await this.loadChunks(false);
        if (this.chunks.length > beforeLength) {
          this.selectChunk(this.chunks[beforeLength]);
        }
      }
    },
    docStatus(doc) {
      if (!doc) return { label: "Tidak diketahui", tone: "muted" };
      const job = this.docJob(doc);
      if (job) {
        return kbJobStatusMeta(job.status);
      }
      const summary = this.summaryMap[String(doc.id)];
      if (summary) {
        return { label: summary.display_status, tone: summary.status_tone };
      }
      const raw = String(doc.status || "").toLowerCase();
      if (raw === "success") return { label: "Selesai diproses", tone: "neutral" };
      if (raw === "processing") return { label: "Sedang diproses", tone: "processing" };
      if (raw === "failed") return { label: "Gagal diproses", tone: "danger" };
      if (raw === "pending") return { label: "Menunggu diproses", tone: "muted" };
      return { label: "Perlu diperiksa", tone: "warning" };
    },
    docStatusKey(doc) {
      const tone = this.docStatus(doc).tone;
      if (tone === "success") return "stored";
      if (tone === "processing") return "processing";
      if (tone === "danger") return "failed";
      if (tone === "muted") return "pending";
      return "attention";
    },
    docChunkLabel(doc) {
      const job = this.docJob(doc);
      if (job) {
        if (job.progress.total > 0) {
          return `${this.formatNumber(job.progress.completed)} dari ${this.formatNumber(job.progress.total)} bagian`;
        }
        return `${this.formatNumber(job.progress.completed)} bagian`;
      }
      const summary = this.summaryMap[String(doc.id)];
      if (summary) {
        if (summary.expected_chunks > 0) {
          return `${this.formatNumber(summary.stored_chunks)} dari ${this.formatNumber(summary.expected_chunks)} bagian`;
        }
        return `${this.formatNumber(summary.stored_chunks)} bagian`;
      }
      return this.summaryLoading ? "Memeriksa bagian..." : "—";
    },
    docAttention(doc) {
      const summary = this.summaryMap[String(doc.id)];
      if (!summary || !summary.needs_attention) return "";
      return summary.safe_message;
    },
    fileKind(doc) {
      const type = String((doc && (doc.file_type || doc.file_name)) || "").toLowerCase();
      if (type.indexOf("pdf") !== -1) return "pdf";
      if (type.indexOf("doc") !== -1) return "doc";
      if (type.indexOf("txt") !== -1) return "txt";
      return "file";
    },
    fileIcon(doc) {
      const kind = this.fileKind(doc);
      if (kind === "pdf") return "fa-file-pdf-o";
      if (kind === "doc") return "fa-file-word-o";
      if (kind === "txt") return "fa-file-text-o";
      return "fa-file-o";
    },
    fileTypeLabel(doc) {
      if (!doc) return "—";
      const kind = this.fileKind(doc);
      if (kind === "pdf") return "PDF";
      if (kind === "doc") return "Dokumen";
      if (kind === "txt") return "TXT";
      const type = String(doc.file_type || "").replace(/^\./, "").toUpperCase();
      return type || "—";
    },
    safeFileUrl(url) {
      if (!url) return "";
      const value = String(url);
      if (value.indexOf("https://") === 0 || value.indexOf("http://") === 0) return value;
      return "";
    },
    openUpload() {
      if (this.isLimitReached) {
        this.$fire({
          title: "Batas Knowledge Base",
          text: "Lisensi ini sudah memiliki 5 Knowledge Base. Hapus salah satu dokumen sebelum menambah file baru.",
          type: "warning",
        });
        return;
      }
      this.showUploadModal = true;
    },
    async onUploaded(licenseId, uploadMeta) {
      this.showUploadModal = false;
      await this.refreshAfterUpload(licenseId, uploadMeta);
    },
    openEditor() {
      if (!this.selectedDoc) return;
      if (!this.canEditSelectedDoc) {
        this.$fire({
          title: "Dokumen sedang diproses",
          text:
            this.selectedDocEditDisabledReason ||
            "Edit Isi tersedia setelah pemrosesan dokumen selesai.",
          type: "warning",
          timer: 2800,
        });
        return;
      }
      this.editorDoc = this.selectedDoc;
      this.showEditorModal = true;
    },
    closeEditor() {
      this.showEditorModal = false;
      this.editorDoc = null;
    },
    onEditorContentLoaded(data) {
      if (!data) return;
      const doc = this.editorDoc || this.selectedDoc;
      if (!doc) return;
      const isProcessing = kbEditorContentIsProcessing(data);
      if (typeof console !== "undefined" && console.info) {
        console.info("[kb-editor] content-loaded", {
          file_id: doc.id,
          is_processing: isProcessing,
          pending_job_active: data.pending_job_active === true,
          pending_job_id: data.pending_job_id || "",
          running_job_id: data.running_job_id || "",
        });
      }
      // Tahap B: periksa job hanya jika backend menyatakan ada proses aktif.
      // 404 jobs/latest untuk dokumen legacy tidak boleh mengunci editor.
      if (isProcessing) {
        this.resolveDocJob(doc);
      } else {
        this.clearStaleActiveDocJob(doc);
      }
    },
    onEditorJobCreated(job) {
      if (!job) return;
      this.applyJob(job, { force: true });
      if (job.job_id && isKbJobActive(job.status)) {
        this.jobMonitor.track(job.job_id);
      }
      this.jobPollError = "";
    },
    onEditorJobSuccess(job) {
      if (!job) return;
      this.applyJob(job);
      // Muat ulang daftar agar metadata/status dokumen dan jumlah bagian
      // mengikuti versi terbaru. Isi tab konten dimuat ulang oleh monitor job.
      this.loadDocs({ preserveSelection: true });
    },
    onEditorJobFailed(job) {
      if (!job) return;
      this.applyJob(job);
      this.loadSummary();
    },
    async refreshAll() {
      if (!this.selectedLicense) return;
      await this.loadDocs({ preserveSelection: true });
    },
    async refreshAfterUpload(licenseId, uploadMeta) {
      if (licenseId) {
        let target = this.licenseOptions.find((item) => String(item.id) === String(licenseId));
        if (!target) {
          await this.searchLicenses("");
          target = this.licenseOptions.find((item) => String(item.id) === String(licenseId));
        }
        if (target) {
          if (!this.selectedLicense || String(this.selectedLicense.id) !== String(target.id)) {
            this.selectedDoc = null;
            this.docs = [];
            this.summary = null;
            this.summaryError = "";
            this.resetChunkState();
            this.resetJobState();
          }
          this.selectedLicense = target;
        }
      }
      this.fileNameFilter = "";
      this.statusFilter = "";
      this.clearSearch();
      await this.loadDocs();
      if (uploadMeta && uploadMeta.file_id) {
        const uploadedDoc = this.docs.find((doc) => Number(doc.id) === Number(uploadMeta.file_id));
        if (uploadedDoc && (!this.selectedDoc || this.selectedDoc.id !== uploadedDoc.id)) {
          await this.selectDoc(uploadedDoc);
        }
      }
      if (uploadMeta && uploadMeta.job_id) {
        this.jobMonitor.track(uploadMeta.job_id);
      }
    },
    async runKbSearch() {
      if (!this.canSearch) return;
      this.kbSearchLoading = true;
      this.kbSearchError = "";
      this.searchActive = true;
      this.kbSearchResults = [];
      this.kbSearchLimited = false;
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseSearch", {
          license_id: this.selectedLicense.id,
          query: this.kbSearchQuery,
          limit: 20,
        });
        const data = response.data || {};
        this.kbSearchResults = data.hits || [];
        this.kbSearchLimited = !!data.limited;
      } catch (error) {
        this.kbSearchError = this.errorMessage(error, "Pencarian isi Knowledge Base belum berhasil.");
      } finally {
        this.kbSearchLoading = false;
      }
    },
    clearSearch() {
      this.kbSearchQuery = "";
      this.kbSearchResults = [];
      this.kbSearchError = "";
      this.kbSearchLimited = false;
      this.searchActive = false;
      this.kbSearchLoading = false;
    },
    async openSearchHit(hit) {
      if (!hit) return;
      const doc = this.docs.find((item) => Number(item.id) === Number(hit.file_id));
      if (!doc) {
        this.kbSearchError = "Dokumen asal hasil pencarian tidak lagi tersedia. Muat ulang halaman.";
        return;
      }
      this.selectedDoc = doc;
      this.activeTab = "content";
      this.resetChunkState();
      this.resetJobChunks();
      const position = Number(hit.position) || Math.max(Number(hit.chunk_index) + 1, 1);
      await this.loadChunks(true, {
        afterIndex: Number(hit.chunk_index) - 1,
        offset: Math.max(position - 1, 0),
        selectChunkIndex: Number(hit.chunk_index),
      });
      this.ensureJobChunks();
      this.$nextTick(() => {
        const element = this.$refs.detailCard;
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    async confirmDelete(doc) {
      if (!doc || this.deletingId) return;
      const confirmed = await this.$fire({
        title: "Hapus dokumen Knowledge Base?",
        text: `Dokumen "${doc.file_name}" akan dihapus dari Knowledge Base lisensi ini. Chat AI tidak akan memakai bagian teksnya lagi.`,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
      });
      if (!confirmed.value) return;
      this.deletingId = doc.id;
      try {
        await this.$store.dispatch("aiKey/deleteKB", {
          id: Number(doc.id),
          license_id: this.selectedLicense.id,
        });
        const jobKey = String(doc.id);
        const deletedJob = this.docJobs[jobKey];
        if (deletedJob && deletedJob.job_id) {
          this.jobMonitor.untrack(deletedJob.job_id);
        }
        this.$delete(this.docJobs, jobKey);
        this.$delete(this.docJobLoading, jobKey);
        this.$delete(this.docJobMissing, jobKey);
        this.$delete(this.docJobError, jobKey);
        if (this.selectedDoc && this.selectedDoc.id === doc.id) {
          this.selectedDoc = null;
          this.resetChunkState();
          this.resetJobChunks();
        }
        await this.$fire({
          title: "Dokumen dihapus",
          text: "Data dokumen dan bagian teksnya berhasil dihapus dari Knowledge Base.",
          type: "success",
          timer: 1800,
        });
        await this.loadDocs({ preserveSelection: true });
      } catch (error) {
        this.$fire({
          title: "Gagal menghapus dokumen",
          text: this.errorMessage(error, "Dokumen belum berhasil dihapus. Coba lagi atau hubungi admin."),
          type: "error",
        });
      } finally {
        this.deletingId = 0;
      }
    },
  },
};
</script>

<style scoped>
.kb-page {
  padding-bottom: 44px;
  color: #344054;
}
.kb-page .content {
  max-width: 1560px;
}
.kb-page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;
}
.kb-page-head-copy h3 {
  margin: 2px 0 5px;
  color: #172033;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.kb-page-head-copy p {
  max-width: 720px;
  margin: 0;
  color: #7b8ba3;
  font-size: 13px;
  line-height: 1.55;
}
.kb-eyebrow {
  display: block;
  color: #98a2b3;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.kb-page-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.kb-btn-primary,
.kb-btn-ghost,
.kb-btn-danger-ghost,
.kb-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 800;
  transition: all 0.16s ease;
}
.kb-btn-primary {
  border: 1px solid #fe9b44;
  background: #fe9b44;
  color: #fff;
  padding: 9px 14px;
}
.kb-btn-primary:hover:not(:disabled) {
  border-color: #e77f25;
  background: #e77f25;
  color: #fff;
}
.kb-btn-ghost {
  border: 1px solid #e1e6ee;
  background: #fff;
  color: #475467;
  padding: 8px 12px;
}
.kb-btn-ghost:hover:not(:disabled) {
  border-color: #f7c98b;
  background: #fffaf5;
  color: #b45309;
}
.kb-btn-danger-ghost {
  border: 1px solid #f6c9c3;
  background: #fff;
  color: #b42318;
  padding: 8px 12px;
}
.kb-btn-danger-ghost:hover:not(:disabled) {
  background: #fff7f5;
}
.kb-btn-primary:disabled,
.kb-btn-ghost:disabled,
.kb-btn-danger-ghost:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.kb-icon-btn {
  width: 31px;
  height: 31px;
  border: 1px solid #e1e6ee;
  background: #fff;
  color: #475467;
  padding: 0;
}
.kb-icon-btn:hover:not(:disabled) {
  border-color: #f7c98b;
  color: #b45309;
}
.kb-license-card {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  background: #fff;
  padding: 12px 14px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 0.035);
}
.kb-license-select {
  flex: 1 1 340px;
  max-width: 430px;
}
.kb-field-label {
  display: block;
  margin-bottom: 4px;
  color: #475467;
  font-size: 10px;
  font-weight: 800;
}
.kb-license-select .multiselect {
  min-height: 36px;
  border-radius: 8px;
}
.kb-license-select .multiselect__tags {
  min-height: 36px;
  border: 1px solid #e1e6ee;
  border-radius: 8px;
  padding: 5px 38px 0 10px;
}
.kb-license-select .multiselect__placeholder,
.kb-license-select .multiselect__single {
  margin-bottom: 0;
  padding-top: 1px;
  color: #667085;
  font-size: 12px;
  line-height: 24px;
}
.kb-license-meta {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 170px;
  border-left: 1px solid #eef1f5;
  padding-left: 14px;
}
.kb-connection-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d0d5dd;
}
.kb-connection-dot.is-online {
  background: #12b76a;
  box-shadow: 0 0 0 3px #e8f7ee;
}
.kb-license-meta strong,
.kb-license-meta small {
  display: block;
}
.kb-license-meta strong {
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.kb-license-meta small {
  margin-top: 1px;
  color: #98a2b3;
  font-size: 10px;
}
.kb-quota-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: #f5f9ff;
  color: #175cd3;
  padding: 7px 10px;
  font-size: 10px;
  font-weight: 700;
}
.kb-quota-pill.is-limit {
  background: #fff7ed;
  color: #b45309;
}
.kb-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.kb-stat-card {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e7eaf0;
  border-radius: 13px;
  background: #fff;
  padding: 13px 14px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 0.035);
}
.kb-stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-size: 15px;
}
.kb-stat-icon.is-orange { background: #fff3e3; color: #e77f25; }
.kb-stat-icon.is-blue { background: #eff6ff; color: #2e90fa; }
.kb-stat-icon.is-green { background: #e8f7ee; color: #0b8f4d; }
.kb-stat-icon.is-yellow { background: #fff8e8; color: #b45309; }
.kb-stat-card strong {
  display: block;
  color: #172033;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
}
.kb-stat-card small {
  display: block;
  margin-top: 2px;
  color: #7b8ba3;
  font-size: 10px;
  font-weight: 700;
}
.kb-inline-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 9px;
  padding: 9px 11px;
  font-size: 11px;
  line-height: 1.45;
}
.kb-inline-alert.is-warning {
  border: 1px solid #f7dfb0;
  background: #fffaf0;
  color: #8a5a00;
}
.kb-inline-alert.is-danger {
  border: 1px solid #f6c9c3;
  background: #fff8f7;
  color: #b42318;
}
.kb-inline-alert .btn {
  margin-left: auto;
}
.kb-search-card {
  margin: 14px 0;
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  background: #fff;
  padding: 14px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 0.035);
}
.kb-search-copy {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 9px;
}
.kb-search-copy strong {
  color: #344054;
  font-size: 13px;
  font-weight: 800;
}
.kb-search-copy small {
  color: #98a2b3;
  font-size: 10px;
}
.kb-search-form {
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #e1e6ee;
  border-radius: 9px;
  background: #fbfcfe;
  padding: 5px 6px 5px 11px;
}
.kb-search-form > i {
  color: #98a2b3;
  font-size: 12px;
}
.kb-search-form input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  background: transparent;
  color: #344054;
  font-size: 12px;
  outline: none;
}
.kb-search-form input::placeholder {
  color: #98a2b3;
}
.kb-search-results {
  margin-top: 10px;
  border-top: 1px solid #eef1f5;
  padding-top: 9px;
}
.kb-search-loading,
.kb-search-empty {
  padding: 10px 2px;
  color: #667085;
  font-size: 11px;
}
.kb-search-result-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
  color: #667085;
  font-size: 10px;
}
.kb-search-result-head small {
  color: #98a2b3;
}
.kb-search-hit {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  width: 100%;
  margin-bottom: 5px;
  border: 1px solid #eef1f5;
  border-radius: 9px;
  background: #fff;
  padding: 9px 10px;
  text-align: left;
  transition: all 0.15s ease;
}
.kb-search-hit:hover {
  border-color: #f7c98b;
  background: #fffaf5;
}
.kb-search-hit-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #fff3e3;
  color: #e77f25;
  font-size: 12px;
}
.kb-search-hit-body {
  flex: 1 1 auto;
  min-width: 0;
}
.kb-search-hit-body strong,
.kb-search-hit-body small {
  display: block;
}
.kb-search-hit-body strong {
  color: #344054;
  font-size: 11px;
  font-weight: 800;
}
.kb-search-hit-body small {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 2px;
  color: #7b8ba3;
  font-size: 10px;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.kb-search-hit-meta {
  flex: 0 0 auto;
  color: #b45309;
  font-size: 9px;
  font-weight: 800;
}
.kb-workspace {
  display: grid;
  grid-template-columns: 380px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}
.kb-doc-card,
.kb-detail-card {
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.04);
}
.kb-doc-card {
  overflow: hidden;
}
.kb-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 9px;
  border-bottom: 1px solid #eef1f5;
  padding: 14px 14px 11px;
}
.kb-panel-head h4 {
  margin: 2px 0 0;
  color: #172033;
  font-size: 14px;
  font-weight: 800;
}
.kb-count-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #fff3e3;
  color: #b45309;
  font-size: 10px;
  font-weight: 800;
}
.kb-doc-filters {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
}
.kb-input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  border: 1px solid #e1e6ee;
  border-radius: 8px;
  background: #fbfcfe;
  padding: 0 9px;
}
.kb-input-wrap i {
  color: #98a2b3;
  font-size: 11px;
}
.kb-input-wrap input {
  width: 100%;
  height: 32px;
  border: 0;
  background: transparent;
  color: #344054;
  font-size: 11px;
  outline: none;
}
.kb-doc-filters .form-control {
  flex: 0 0 120px;
  height: 34px;
  border-color: #e1e6ee;
  color: #475467;
  font-size: 10px;
}
.kb-doc-list {
  min-height: 230px;
  max-height: 590px;
  overflow-y: auto;
  padding: 0 8px 8px;
}
.kb-doc-item {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  width: 100%;
  margin-bottom: 5px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  padding: 9px;
  text-align: left;
  transition: all 0.15s ease;
}
.kb-doc-item:hover {
  background: #fbfcfe;
}
.kb-doc-item.is-active {
  border-color: #f7c98b;
  background: #fffaf5;
  box-shadow: inset 3px 0 0 #fe9b44;
}
.kb-file-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  font-size: 14px;
}
.kb-file-icon.is-pdf { background: #fff1ed; color: #d92d20; }
.kb-file-icon.is-doc { background: #eff6ff; color: #2e90fa; }
.kb-file-icon.is-txt { background: #f2f4f7; color: #667085; }
.kb-file-icon.is-file { background: #fff3e3; color: #e77f25; }
.kb-file-icon.is-large {
  flex-basis: 42px;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  font-size: 18px;
}
.kb-doc-body {
  flex: 1 1 auto;
  min-width: 0;
}
.kb-doc-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
}
.kb-doc-title-row strong {
  overflow: hidden;
  color: #344054;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kb-status-badge {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 3px 6px;
  font-size: 8px;
  font-weight: 800;
  white-space: nowrap;
}
.kb-status-badge.is-large {
  padding: 5px 8px;
  font-size: 9px;
}
.kb-status-badge.is-success,
.kb-status-badge.is-green { background: #e8f7ee; color: #0b8f4d; }
.kb-status-badge.is-warning { background: #fff4e8; color: #b45309; }
.kb-status-badge.is-danger { background: #fff1ed; color: #b42318; }
.kb-status-badge.is-processing { background: #eff6ff; color: #175cd3; }
.kb-status-badge.is-muted,
.kb-status-badge.is-neutral { background: #f2f4f7; color: #667085; }
.kb-doc-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 3px;
  color: #98a2b3;
  font-size: 9px;
}
.kb-doc-meta i {
  margin-right: 2px;
}
.kb-doc-attention {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 3px;
  color: #b45309;
  font-size: 9px;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.kb-doc-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: #98a2b3;
  font-size: 11px;
  transition: all 0.15s ease;
}
.kb-doc-delete:hover {
  background: #fff1ed;
  color: #b42318;
}
.kb-doc-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid #eef1f5;
  background: #fbfcfe;
  padding: 8px 12px;
  color: #98a2b3;
  font-size: 9px;
}
.kb-doc-footer .is-warning {
  color: #b45309;
  font-weight: 800;
}
.kb-doc-skeleton {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 9px;
}
.kb-doc-skeleton > span {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #f2f4f7;
  animation: kb-pulse 1.3s infinite;
}
.kb-doc-skeleton > div {
  flex: 1 1 auto;
}
.kb-doc-skeleton i {
  display: block;
  height: 8px;
  margin-bottom: 5px;
  border-radius: 4px;
  background: #f2f4f7;
  animation: kb-pulse 1.3s infinite;
}
.kb-doc-skeleton i:last-child {
  width: 62%;
}
@keyframes kb-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}
.kb-state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 190px;
  padding: 20px;
  text-align: center;
}
.kb-state-box > i {
  margin-bottom: 8px;
  color: #98a2b3;
  font-size: 24px;
}
.kb-state-box strong {
  color: #475467;
  font-size: 12px;
  font-weight: 800;
}
.kb-state-box span {
  max-width: 300px;
  margin-top: 4px;
  color: #98a2b3;
  font-size: 10px;
  line-height: 1.5;
}
.kb-state-box .btn {
  margin-top: 10px;
}
.kb-state-box.is-error > i {
  color: #d92d20;
}
.kb-state-box.is-empty > i {
  color: #e77f25;
}
.kb-state-box.is-compact {
  min-height: 120px;
  padding: 12px;
}
.kb-detail-card {
  min-width: 0;
  overflow: hidden;
}
.kb-detail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 520px;
  padding: 30px;
  text-align: center;
}
.kb-detail-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 10px;
  border-radius: 16px;
  background: #fff3e3;
  color: #e77f25;
  font-size: 24px;
}
.kb-detail-empty strong {
  color: #475467;
  font-size: 14px;
  font-weight: 800;
}
.kb-detail-empty span {
  max-width: 380px;
  margin-top: 4px;
  color: #98a2b3;
  font-size: 11px;
  line-height: 1.5;
}
.kb-detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  border-bottom: 1px solid #eef1f5;
  padding: 15px 16px;
}
.kb-detail-title {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  min-width: 0;
}
.kb-detail-title h4 {
  overflow: hidden;
  max-width: 640px;
  margin: 2px 0 4px;
  color: #172033;
  font-size: 17px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kb-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  color: #7b8ba3;
  font-size: 9px;
}
.kb-detail-meta i {
  margin-right: 2px;
}
.kb-detail-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  flex: 0 0 auto;
}
.kb-detail-actions .btn {
  font-size: 10px;
  padding: 7px 9px;
}
.kb-edit-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0 14px 10px;
  padding: 7px 9px;
  border: 1px solid #f6d49b;
  border-radius: 8px;
  background: #fffaf1;
  color: #8f5600;
  font-size: 10.5px;
  line-height: 1.5;
}
.kb-edit-hint i {
  margin-top: 2px;
  color: #e77f25;
}
.kb-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #eef1f5;
  padding: 0 14px;
}
.kb-tabs button {
  position: relative;
  border: 0;
  background: transparent;
  color: #7b8ba3;
  padding: 11px 8px 9px;
  font-size: 11px;
  font-weight: 800;
}
.kb-tabs button.active {
  color: #b45309;
}
.kb-tabs button.active::after {
  content: "";
  position: absolute;
  right: 7px;
  bottom: -1px;
  left: 7px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: #fe9b44;
}
.kb-tabs button i {
  margin-right: 4px;
}
.kb-content-tab,
.kb-info-tab {
  padding: 14px;
}
.kb-content-loading {
  padding: 40px 10px;
  color: #7b8ba3;
  font-size: 11px;
  text-align: center;
}
.kb-content-grid {
  display: grid;
  grid-template-columns: minmax(250px, 0.78fr) minmax(0, 1.22fr);
  gap: 12px;
  min-height: 440px;
}
.kb-chunk-panel,
.kb-preview-panel {
  border: 1px solid #eef1f5;
  border-radius: 11px;
  background: #fbfcfe;
}
.kb-chunk-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 10px;
}
.kb-subhead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
}
.kb-subhead strong {
  display: block;
  margin-top: 1px;
  color: #344054;
  font-size: 11px;
  font-weight: 800;
}
.kb-chunk-list {
  flex: 1 1 auto;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 2px;
}
.kb-chunk-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin-bottom: 5px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: #fff;
  padding: 8px;
  text-align: left;
  transition: all 0.15s ease;
}
.kb-chunk-item:hover {
  border-color: #f7c98b;
}
.kb-chunk-item.is-active {
  border-color: #f7c98b;
  background: #fffaf5;
}
.kb-chunk-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  background: #fff3e3;
  color: #b45309;
  font-size: 9px;
  font-weight: 800;
}
.kb-chunk-body {
  flex: 1 1 auto;
  min-width: 0;
}
.kb-chunk-body strong,
.kb-chunk-body small,
.kb-chunk-body em {
  display: block;
}
.kb-chunk-body strong {
  color: #344054;
  font-size: 10px;
  font-weight: 800;
}
.kb-chunk-body small {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 1px;
  color: #7b8ba3;
  font-size: 9px;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.kb-chunk-body em {
  margin-top: 3px;
  color: #98a2b3;
  font-size: 8px;
  font-style: normal;
}
.kb-load-more {
  width: 100%;
  margin-top: 6px;
  font-size: 10px;
}
.kb-preview-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 11px;
}
.kb-preview-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.kb-preview-head strong {
  display: block;
  margin-top: 1px;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.kb-preview-nav {
  display: flex;
  gap: 4px;
}
.kb-preview-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 9px 0;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #f5f9ff;
  color: #5b7fb9;
  padding: 7px 8px;
  font-size: 9px;
  line-height: 1.45;
}
.kb-preview-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  color: #7b8ba3;
  font-size: 10px;
}
.kb-preview-text {
  flex: 1 1 auto;
  max-height: 440px;
  overflow-y: auto;
  border: 1px solid #eef1f5;
  border-radius: 9px;
  background: #fff;
  color: #344054;
  padding: 13px;
  font-size: 12px;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
}
.kb-info-tab {
  min-height: 440px;
}
.kb-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.kb-info-row {
  border: 1px solid #eef1f5;
  border-radius: 9px;
  background: #fbfcfe;
  padding: 9px 10px;
}
.kb-info-row span,
.kb-info-row strong {
  display: block;
}
.kb-info-row span {
  color: #98a2b3;
  font-size: 9px;
  font-weight: 700;
}
.kb-info-row strong {
  margin-top: 3px;
  color: #344054;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.4;
  word-break: break-word;
}
.kb-info-row strong.is-success { color: #0b8f4d; }
.kb-info-row strong.is-warning { color: #b45309; }
.kb-info-row strong.is-danger { color: #b42318; }
.kb-info-row strong.is-processing { color: #175cd3; }
.kb-info-note {
  margin-top: 10px;
  border: 1px solid #f7dfb0;
  border-radius: 9px;
  background: #fffaf0;
  padding: 10px 11px;
}
.kb-info-note p {
  margin: 4px 0 0;
  color: #7a4b08;
  font-size: 10px;
  line-height: 1.55;
}
.kb-info-note small {
  display: block;
  margin-top: 4px;
  color: #98600b;
  font-size: 9px;
  line-height: 1.45;
}
.kb-info-note.is-muted {
  border-color: #eef1f5;
  background: #fbfcfe;
}
.kb-info-note.is-muted p {
  color: #7b8ba3;
}
@media (max-width: 1200px) {
  .kb-workspace {
    grid-template-columns: 340px minmax(0, 1fr);
  }
  .kb-content-grid {
    grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.2fr);
  }
}
@media (max-width: 991px) {
  .kb-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .kb-workspace {
    grid-template-columns: minmax(0, 1fr);
  }
  .kb-doc-list {
    max-height: 340px;
  }
}
@media (max-width: 767px) {
  .kb-page-head {
    flex-direction: column;
  }
  .kb-page-head-actions {
    width: 100%;
  }
  .kb-page-head-actions .btn {
    flex: 1 1 auto;
  }
  .kb-license-card {
    flex-wrap: wrap;
  }
  .kb-license-select {
    max-width: none;
    flex-basis: 100%;
  }
  .kb-license-meta {
    border-left: 0;
    padding-left: 0;
  }
  .kb-quota-pill {
    margin-left: auto;
  }
  .kb-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .kb-stat-card {
    padding: 10px;
  }
  .kb-stat-card strong {
    font-size: 17px;
  }
  .kb-search-form {
    flex-wrap: wrap;
  }
  .kb-search-form input {
    flex-basis: calc(100% - 26px);
  }
  .kb-search-form .btn {
    flex: 1 1 auto;
  }
  .kb-detail-head {
    flex-direction: column;
  }
  .kb-detail-actions {
    justify-content: flex-start;
  }
  .kb-content-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .kb-chunk-list {
    max-height: 270px;
  }
  .kb-preview-text {
    max-height: 360px;
  }
  .kb-info-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 480px) {
  .kb-summary-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .kb-doc-filters {
    flex-direction: column;
  }
  .kb-doc-filters .form-control {
    flex-basis: auto;
  }
  .kb-tabs {
    padding: 0 8px;
  }
  .kb-tabs button {
    flex: 1 1 auto;
    font-size: 10px;
  }
}

/* Monitoring antrean pemrosesan Knowledge Base */
.kb-queue-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  background: #fff;
  padding: 10px 12px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 0.035);
}
.kb-queue-strip.is-active {
  border-color: #dbeafe;
  background: #f8fbff;
}
.kb-queue-strip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: #eff6ff;
  color: #175cd3;
  font-size: 13px;
}
.kb-queue-strip-body {
  flex: 1 1 auto;
  min-width: 0;
}
.kb-queue-strip-body strong {
  display: block;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.kb-queue-strip-body span {
  display: block;
  margin-top: 1px;
  color: #7b8ba3;
  font-size: 10px;
}
.kb-queue-strip-time {
  flex: 0 0 auto;
  color: #98a2b3;
  font-size: 9px;
}
.kb-doc-queue {
  display: block;
  margin-top: 4px;
}
.kb-doc-queue-bar {
  display: block;
  overflow: hidden;
  height: 4px;
  border-radius: 999px;
  background: #eef2f6;
}
.kb-doc-queue-bar > span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #2e90fa;
  transition: width 0.25s ease;
}
.kb-doc-queue-bar > span.is-success { background: #12b76a; }
.kb-doc-queue-bar > span.is-danger { background: #f04438; }
.kb-doc-queue-bar > span.is-warning { background: #f79009; }
.kb-doc-queue-bar > span.is-muted,
.kb-doc-queue-bar > span.is-neutral { background: #98a2b3; }
.kb-doc-queue em {
  display: block;
  margin-top: 3px;
  color: #7b8ba3;
  font-size: 8px;
  font-style: normal;
  font-weight: 700;
}
.kb-job-card {
  margin: 12px 14px 0;
  border: 1px solid #e7eaf0;
  border-radius: 11px;
  background: #fbfcfe;
  padding: 11px 12px;
}
.kb-job-card.is-success { border-color: #c9ecd8; background: #f7fdf9; }
.kb-job-card.is-danger { border-color: #f6c9c3; background: #fffaf9; }
.kb-job-card.is-warning { border-color: #f7dfb0; background: #fffcf5; }
.kb-job-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.kb-job-card-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 3px;
}
.kb-job-card-title strong {
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.kb-job-card-head small {
  display: block;
  margin-top: 3px;
  color: #7b8ba3;
  font-size: 10px;
  line-height: 1.45;
}
.kb-job-card-time {
  flex: 0 0 auto;
  margin-top: 0 !important;
  color: #98a2b3 !important;
  font-size: 9px !important;
}
.kb-job-progress {
  overflow: hidden;
  height: 6px;
  margin-top: 9px;
  border-radius: 999px;
  background: #eef2f6;
}
.kb-job-progress > span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #2e90fa;
  transition: width 0.25s ease;
}
.kb-job-progress > span.is-success { background: #12b76a; }
.kb-job-progress > span.is-danger { background: #f04438; }
.kb-job-progress > span.is-warning { background: #f79009; }
.kb-job-progress > span.is-muted,
.kb-job-progress > span.is-neutral { background: #98a2b3; }
.kb-job-counts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px 14px;
  margin-top: 8px;
  color: #7b8ba3;
  font-size: 9px;
  font-weight: 700;
}
.kb-job-counts span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.kb-job-counts strong {
  color: #344054;
  font-size: 9px;
  font-weight: 800;
}
.kb-job-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #98a2b3;
}
.kb-job-dot.is-success { background: #12b76a; }
.kb-job-dot.is-processing { background: #2e90fa; }
.kb-job-dot.is-danger { background: #f04438; }
.kb-job-error {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 8px 0 0;
  color: #b42318;
  font-size: 10px;
  line-height: 1.45;
}
.kb-job-chunks {
  margin-bottom: 12px;
  border: 1px solid #eef1f5;
  border-radius: 11px;
  background: #fbfcfe;
  padding: 10px;
}
.kb-job-chunks-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
}
.kb-job-chunks-head strong {
  display: block;
  margin-top: 1px;
  color: #344054;
  font-size: 11px;
  font-weight: 800;
}
.kb-job-chunks-loading,
.kb-job-chunks-empty {
  padding: 12px 4px;
  color: #7b8ba3;
  font-size: 10px;
}
.kb-job-chunk-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 5px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 2px;
}
.kb-job-chunk {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  border: 1px solid #eef1f5;
  border-radius: 9px;
  background: #fff;
  padding: 7px 8px;
}
.kb-job-chunk.is-danger {
  border-color: #f6c9c3;
  background: #fffaf9;
}
.kb-job-chunk.is-processing {
  border-color: #dbeafe;
}
.kb-job-chunk-body {
  flex: 1 1 auto;
  min-width: 0;
}
.kb-job-chunk-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 5px;
}
.kb-job-chunk-head strong {
  color: #344054;
  font-size: 10px;
  font-weight: 800;
}
.kb-job-chunk-preview {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 2px;
  color: #7b8ba3;
  font-size: 9px;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.kb-job-chunk-body em {
  display: block;
  margin-top: 3px;
  color: #98a2b3;
  font-size: 8px;
  font-style: normal;
}
.kb-job-chunk-error {
  margin: 4px 0 0;
  color: #b42318;
  font-size: 9px;
  line-height: 1.4;
}
.kb-job-chunk-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}
.kb-job-chunk-pager span {
  color: #7b8ba3;
  font-size: 9px;
  font-weight: 700;
}
.kb-job-chunk-pager .btn {
  font-size: 10px;
  padding: 5px 9px;
}
.kb-info-row strong.is-muted {
  color: #98a2b3;
  font-weight: 700;
}
@media (max-width: 767px) {
  .kb-job-chunk-list {
    grid-template-columns: minmax(0, 1fr);
  }
  .kb-queue-strip {
    align-items: flex-start;
  }
  .kb-queue-strip-time {
    display: none;
  }
  .kb-job-card-head {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/settings/historykb.vue